import React, { useState, useEffect } from 'react';
import { getTokenBalances, filterNonZeroBalances } from '../../utils/tokenBalance';
import { usePaymentTimer, formatTime } from '../../hooks/usePaymentTimer';
import { useReownWallet } from '../../hooks/useReownWallet';

const TokenBalanceFetcher = ({ 
  selectedChain, 
  walletAddress,
  paymentData,
  onBalancesFetched,
  onError,
  onCancel
}) => {
  const [isFetching, setIsFetching] = useState(true);
  const [balances, setBalances] = useState([]);
  const [error, setError] = useState(null);
  const [showBalances, setShowBalances] = useState(false);
  const [isProcessingOptions, setIsProcessingOptions] = useState(false);

  const timeRemaining = usePaymentTimer(paymentData?.expiresAt);
  const { provider } = useReownWallet();

  useEffect(() => {
    fetchBalances();
  }, []);

  const fetchBalances = async () => {
    setIsFetching(true);
    setError(null);
    setShowBalances(false);

    try {
      console.log('💰 Fetching balances for chain:', selectedChain.chainId);

      if (!provider) {
        throw new Error('Wallet provider not available');
      }

      const tokenList = selectedChain.whitelistedTokens.map(symbol => {
        const tokenInfo = selectedChain.tokenInfo?.[symbol];
        
        if (!tokenInfo) {
          console.warn(`Token info not found for ${symbol} on chain ${selectedChain.chainId}`);
          return null;
        }

        return {
          symbol: symbol,
          address: tokenInfo.address,
          decimals: tokenInfo.decimals
        };
      }).filter(Boolean);

      if (tokenList.length === 0) {
        throw new Error('No valid tokens found for this chain');
      }

      console.log('📋 Tokens to check:', tokenList);

      const allBalances = await getTokenBalances(tokenList, walletAddress, provider);
      console.log('💰 All balances:', allBalances);

      const nonZeroBalances = filterNonZeroBalances(allBalances);
      console.log('✅ Non-zero balances:', nonZeroBalances);

      setBalances(nonZeroBalances);
      setIsFetching(false);

      if (nonZeroBalances.length === 0) {
        setError(
          `You don't have any of the accepted tokens on ${selectedChain.name}. ` +
          `Please add tokens to your wallet or select a different network.`
        );
        return;
      }

      setShowBalances(true);
    } catch (err) {
      console.error('❌ Balance fetch error:', err);
      setError(err.message);
      setIsFetching(false);

      if (onError) {
        onError(err);
      }
    }
  };

  const handleProceedToPayment = () => {
    if (balances.length === 0) {
      setError('No balances available to proceed');
      return;
    }

    console.log('📤 User confirmed - sending balances to parent component...');
    setIsProcessingOptions(true);

    if (onBalancesFetched) {
      onBalancesFetched({
        balances: balances,
        chain: selectedChain
      });
    }
  };

  const handleRetry = () => {
    fetchBalances();
  };

  return (
    <div className="token-balance-fetcher">
      <div className={timeRemaining < 30000 ? 'timer timer-warning' : 'timer'}>
        <span style={{ marginRight: '8px' }}>⏰</span>
        <span>Time Remaining: </span>
        <span className="timer-value">{formatTime(timeRemaining)}</span>
      </div>

      {isProcessingOptions && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }} />
          <h2 className="section-title">Calculating Payment Options</h2>
          <p style={{ color: '#666', marginTop: '16px', fontSize: '14px' }}>
            Analyzing your balances and calculating the best payment routes...
          </p>
        </div>
      )}

      {!isProcessingOptions && (
        <>
          <h2 className="section-title">
            {isFetching ? 'Checking Your Balances...' : 'Token Balances'}
          </h2>

          <div className="chain-info-box">
            <span className="chain-info-label">Selected Network:</span>
            <span className="chain-info-value">{selectedChain.name}</span>
          </div>

          {isFetching && !error && (
            <div className="balance-loading">
              <div className="spinner" />
              <p className="loading-text">Reading token balances from blockchain...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              <p>{error}</p>
            </div>
          )}

          {showBalances && balances.length > 0 && (
            <div className="balances-list">
              <h3 className="balances-subtitle">✅ Available Tokens Found</h3>
              
              <div className="balance-cards">
                {balances.map(token => (
                  <div key={token.symbol} className="balance-card">
                    <div className="balance-card-header">
                      <span className="balance-symbol">{token.symbol}</span>
                    </div>
                    <div className="balance-amount">{token.balanceFormatted}</div>
                    {token.error && (
                      <div className="balance-error">Failed to fetch</div>
                    )}
                  </div>
                ))}
              </div>

              <div className="balance-info-box" style={{ 
                marginTop: '20px', 
                padding: '12px', 
                background: '#e3f2fd', 
                borderRadius: '8px',
                border: '1px solid #2196f3'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#1565c0' }}>
                  ✅ Found {balances.length} token{balances.length > 1 ? 's' : ''} with balance.
                  Click "Proceed to Payment" to calculate swap options.
                </p>
              </div>

              <div className="balance-actions" style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                <button 
                  onClick={handleProceedToPayment} 
                  className="btn btn-primary btn-lg"
                  style={{ flex: 1 }}
                >
                  Proceed to Payment Options →
                </button>
              </div>
            </div>
          )}

          <div className="balance-actions" style={{ marginTop: '20px' }}>
            {error && (
              <button onClick={handleRetry} className="btn btn-primary">
                Retry
              </button>
            )}
            
            <button onClick={onCancel} disabled={isFetching} className="btn btn-secondary">
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TokenBalanceFetcher;